import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Alert, Button, Tabs, Tab,
  TextField, InputAdornment, FormControlLabel, Switch, Card,
  CardContent, Chip, Skeleton, alpha, Fade,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
  Radar as ScannerIcon,
  Analytics as AnalyticsIcon,
  BarChart as BacktestIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { legendaryAPI } from '../../services/api';
import SignalTable from './SignalTable';
import ScanSummaryCards from './ScanSummaryCards';
import BacktestSummary from './BacktestSummary';
import StrategyInfoCard from './StrategyInfoCard';

export default function StrategyPage({ strategyKey, title, icon, color = '#3b82f6' }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scanData, setScanData] = useState(null);
  const [showS80, setShowS80] = useState(false);

  const [ticker, setTicker] = useState('');
  const [stockResult, setStockResult] = useState(null);
  const [stockLoading, setStockLoading] = useState(false);

  const gradient = `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.75)} 100%)`;

  const runScan = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const res = await legendaryAPI.scanOne(strategyKey);
      setScanData(res.data);
    } catch (err) { setError(err.response?.data?.detail || err.message); }
    finally { setLoading(false); }
  }, [strategyKey]);

  const analyzeStock = useCallback(async () => {
    if (!ticker.trim()) return;
    try {
      setStockLoading(true);
      const res = await legendaryAPI.analyzeStockOne(ticker.trim().toUpperCase(), strategyKey);
      setStockResult(res.data);
    } catch (err) { setStockResult({ error: err.response?.data?.detail || err.message }); }
    finally { setStockLoading(false); }
  }, [ticker, strategyKey]);

  // Auto-scan on mount
  React.useEffect(() => { runScan(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleKeyDown = (e) => { if (e.key === 'Enter') analyzeStock(); };

  const signals = scanData?.results || [];

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {/* ── Page Header ──────────────────────────────────── */}
      <Box sx={{ mb: 1 }}>
        <Button
          startIcon={<BackIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate('/legendary-strategies')}
          sx={{
            color: 'text.secondary', textTransform: 'none', fontWeight: 600,
            fontSize: '0.82rem', px: 1, py: 0.4, borderRadius: 1.5, mb: 1,
            '&:hover': { bgcolor: alpha('#667eea', 0.06) },
          }}
        >
          Legendary Strategies
        </Button>
      </Box>

      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{
            width: 52, height: 52, borderRadius: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: gradient, color: '#fff',
            boxShadow: `0 4px 16px ${alpha(color, 0.25)}`, mr: 2.5,
          }}>
            {React.cloneElement(icon, { sx: { fontSize: 28 } })}
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.3 }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {strategyKey === 'ONEIL' && 'Breakout khỏi nền giá với volume & RS cao'}
              {strategyKey === 'MINERVINI' && 'Trend Template + VCP contraction'}
              {strategyKey === 'WEINSTEIN' && 'Pullback về MA150 trong uptrend Stage 2'}
              {strategyKey === 'TURTLE' && 'Breakout kênh Donchian 20/55 ngày'}
              {strategyKey === 'LIVERMORE' && 'Vượt pivotal point với volume xác nhận'}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained" startIcon={<RefreshIcon />}
          onClick={runScan} disabled={loading}
          sx={{
            background: gradient, px: 3, py: 1, fontWeight: 600, borderRadius: 2,
            boxShadow: `0 3px 10px ${alpha(color, 0.25)}`,
            '&:hover': { boxShadow: `0 4px 14px ${alpha(color, 0.35)}` },
          }}
        >
          {loading ? 'Đang quét...' : 'Quét Universe'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      {/* Strategy Info (collapsible) */}
      <StrategyInfoCard strategy={strategyKey} />

      {/* ── Tabs ─────────────────────────────────────────── */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
          px: 1,
          '& .MuiTab-root': { minHeight: 48, fontWeight: 600, fontSize: '0.88rem', textTransform: 'none', gap: 0.75 },
          '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', bgcolor: color },
        }}>
          <Tab icon={<ScannerIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Scanner" />
          <Tab icon={<AnalyticsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Phân Tích Mã" />
          <Tab icon={<BacktestIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Backtest" />
        </Tabs>
      </Card>

      {/* ── Tab 0: Scanner ───────────────────────────────── */}
      {tab === 0 && (
        <Box>
          {loading && <ScanSummaryCards loading />}
          {!loading && scanData && (
            <ScanSummaryCards scanData={{
              scanned: scanData.total_scanned || 0,
              signals: signals.length,
              topScore: signals.length > 0 ? Math.max(...signals.map(s => s.score)) : 0,
              elapsed: scanData.scan_time_s || 0,
            }} />
          )}
          <SignalTable signals={signals} loading={loading} />
        </Box>
      )}

      {/* ── Tab 1: Phân Tích Mã ──────────────────────────── */}
      {tab === 1 && (
        <Box>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1.5 }}>
              Phân tích riêng từng mã
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, maxWidth: 480 }}>
              <TextField
                size="small" fullWidth
                placeholder="Nhập mã CK (VD: FPT, VNM...)"
                value={ticker}
                onChange={(e) => setTicker(e.target.value.toUpperCase())}
                onKeyDown={handleKeyDown}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
              <Button
                variant="contained" onClick={analyzeStock}
                disabled={stockLoading || !ticker.trim()}
                sx={{
                  bgcolor: color, minWidth: 110, borderRadius: 1.5, fontWeight: 600,
                  '&:hover': { bgcolor: color, filter: 'brightness(0.92)' },
                }}
              >
                Phân Tích
              </Button>
            </Box>
          </Card>

          {stockLoading && (
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Skeleton variant="rounded" width={48} height={22} sx={{ borderRadius: 1 }} />
                <Skeleton width={200} height={18} sx={{ borderRadius: 0.5 }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Skeleton variant="rounded" width={60} height={20} sx={{ borderRadius: 1 }} />
                <Skeleton variant="rounded" width={80} height={20} sx={{ borderRadius: 1 }} />
                <Skeleton variant="rounded" width={70} height={20} sx={{ borderRadius: 1 }} />
              </Box>
            </Card>
          )}

          {stockResult && !stockResult.error && (
            <Fade in>
              <Box>
                {stockResult.signal ? (
                  <>
                    <Alert severity="success" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                      <strong>{stockResult.ticker}</strong> — Tín hiệu BUY! Score: {stockResult.score}
                    </Alert>
                    <SignalTable signals={[stockResult]} />
                  </>
                ) : (
                  <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                    <strong>{stockResult.ticker}</strong>: Không có tín hiệu — {stockResult.reason || 'Chưa đủ điều kiện'}
                  </Alert>
                )}
                {stockResult.reasons?.length > 0 && (
                  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mt: 2 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Chi tiết</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {stockResult.reasons.map((r, i) => (
                          <Chip key={i} label={r} size="small" variant="outlined"
                            sx={{ fontSize: '0.72rem', borderColor: '#e2e8f0', color: '#64748b' }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </Fade>
          )}
          {stockResult?.error && <Alert severity="error" sx={{ borderRadius: 2 }}>{stockResult.error}</Alert>}
        </Box>
      )}

      {/* ── Tab 2: Backtest ──────────────────────────────── */}
      {tab === 2 && (
        <Box>
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={<Switch checked={showS80} onChange={(e) => setShowS80(e.target.checked)} size="small" />}
              label={<Typography variant="body2" fontWeight={600}>Chỉ hiện Score ≥ 80</Typography>}
            />
          </Box>
          <BacktestSummary strategy={strategyKey} showS80={showS80} color={color} />
        </Box>
      )}
    </Box>
  );
}
